import { api } from '@/lib/axios'
import { mitStand } from '@/lib/speichern'
import type { CalcCostArticle, CalcCostLine, CalcSheet, CalcWorkStep } from '../types/calculation.types'

/** AP-37 8. — Kalkulationsblatt (`/calculations/:id/positions/:pid/sheet`, Kostenzeilen, Arbeitsschritte, Aufschläge). */
const blatt = (id: string, pid: string) => `/calculations/${id}/positions/${pid}`

export const fetchSheet = (id: string, pid: string) => api.get<{ data: CalcSheet }>(`${blatt(id, pid)}/sheet`).then((r) => r.data.data)

export const costLineUrl = (id: string, pid: string, lid: string) => `${blatt(id, pid)}/cost-lines/${lid}`
export const workStepUrl = (id: string, pid: string, sid: string) => `${blatt(id, pid)}/work-steps/${sid}`
export const surchargeUrl = (id: string, pid: string, sid: string) => `${blatt(id, pid)}/surcharges/${sid}`

/** N3-2: in kg umrechnen oder zurück — eine Handlung mit Stand wie ein PATCH. */
export const convertCostLine = (id: string, pid: string, lid: string, to: 'WEIGHT' | 'ORIGINAL', stand?: string) =>
  api
    .post<{ data: CalcCostLine }>(`${costLineUrl(id, pid, lid)}/convert`, { to }, mitStand(stand))
    .then((r) => r.data.data)

export const createCostLine = (id: string, pid: string, dto: Record<string, unknown>) =>
  api.post<{ data: CalcCostLine }>(`${blatt(id, pid)}/cost-lines`, dto).then((r) => r.data.data)
export const deleteCostLine = (id: string, pid: string, lid: string) => api.delete(costLineUrl(id, pid, lid))
export const reorderCostLines = (id: string, pid: string, ids: string[]) =>
  api.post<{ data: CalcSheet }>(`${blatt(id, pid)}/cost-lines/reorder`, { ids }).then((r) => r.data.data)
/** Nachtrag 4 (N4-2): markierte Kostenzeilen in die Ablage bzw. Artikelzeilen der Ablage ans Ende des Blatts. */
export const copyCostLines = (id: string, pid: string, ids: string[]) =>
  api.post<{ data: { copied: number } }>(`${blatt(id, pid)}/cost-lines/copy`, { ids }).then((r) => r.data.data)
export const pasteCostLines = (id: string, pid: string, itemIds: string[]) =>
  api.post<{ data: CalcSheet; pasted: number }>(`${blatt(id, pid)}/cost-lines/paste`, { itemIds }).then((r) => r.data)
/** Die Einträge der Ablage, die ein Blatt aufnehmen kann: Artikelzeilen (wie „Aus Ablage" in der Stückliste). */
export const fetchClipboardArticleIds = () =>
  api
    .get<{ data: { id: string; position: { lineType?: string } }[] }>('/clipboard')
    .then((r) => r.data.data.filter((e) => e.position.lineType === 'ARTICLE').map((e) => e.id))

export const createWorkStep = (id: string, pid: string, dto: Record<string, unknown>) =>
  api.post<{ data: CalcWorkStep }>(`${blatt(id, pid)}/work-steps`, dto).then((r) => r.data.data)
export const deleteWorkStep = (id: string, pid: string, sid: string) => api.delete(workStepUrl(id, pid, sid))
export const reorderWorkSteps = (id: string, pid: string, ids: string[]) =>
  api.post<{ data: CalcSheet }>(`${blatt(id, pid)}/work-steps/reorder`, { ids }).then((r) => r.data.data)
/** Nachtrag 7 (N7-5): kopierte Arbeitsschritte (Ablage im Browser) in dieses Blatt — vor `beforeId` oder ans Ende. */
export const pasteWorkSteps = (id: string, pid: string, stepIds: string[], beforeId?: string) =>
  api.post<{ data: CalcSheet; pasted: number }>(`${blatt(id, pid)}/work-steps/paste`, { stepIds, ...(beforeId ? { beforeId } : {}) }).then((r) => r.data)

export const applySheet = (id: string, dto: { fromPositionId: string; toPositionIds: string[]; surcharges?: boolean }) =>
  api.post<{ data: { applied: number } }>(`/calculations/${id}/positions/apply-sheet`, dto).then((r) => r.data.data)

export const searchCostArticles = (q: string) =>
  api.get<{ data: CalcCostArticle[] }>('/calculations/lookups/cost-articles', { params: { q } }).then((r) => r.data.data)
export const searchSuppliers = (q: string) =>
  api.get<{ data: { id: string; name: string; clientNumber: string }[] }>('/calculations/lookups/suppliers', { params: { q } }).then((r) => r.data.data)
